import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { getOverrides, useClasses } from '../../../utils/overrides';
import InputWrapper from '../components/InputWrapper';

import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Slider');

function Slider({
    classes: classesProp,
    className: classNameProp,
    error,
    isRange = false,
    isReadOnly = false,
    max = 100,
    min = 0,
    onChange = () => {},
    overrides: overridesProp = {},
    showValue = true,
    step = 1,
    tipFormatter,
    value,
    ...props
}) {
    const classes = useClasses(useStyles, classesProp);
    const sliderRef = useRef(null);
    const [activeHandle, setActiveHandle] = useState(null);
    const override = getOverrides(overridesProp, Slider.overrides);
    const [innerValue, setInnerValue] = useState(
        isRange && (value === undefined || value === null) ? [min, max] : value || min,
    );
    const rootClassName = classnames(
        classes.root,
        {
            [classes.isReadOnly]: isReadOnly,
        },
        classNameProp,
    );

    useEffect(() => {
        if (isRange) {
            if (!value) setInnerValue([min, max]);
            else setInnerValue(value);
        } else {
            setInnerValue(value || min);
        }
    }, [value, isRange, min, max]);

    const getPositionFromValue = useCallback(
        (val) => {
            if (max === min) return 0;
            return ((val - min) / (max - min)) * 100;
        },
        [min, max],
    );

    const getValueFromPosition = useCallback(
        (clientX) => {
            if (!sliderRef.current) return min;
            const rect = sliderRef.current.getBoundingClientRect();
            const percent = (clientX - rect.left) / rect.width;
            const rawValue = percent * (max - min) + min;
            const steppedValue = Math.round(rawValue / step) * step;
            return Math.max(min, Math.min(max, steppedValue));
        },
        [min, max, step],
    );

    const updateValue = useCallback(
        (newValue, handleIndex = null) => {
            if (isRange && handleIndex !== null) {
                const newRangeValue = [...innerValue];
                newRangeValue[handleIndex] = newValue;

                // Ensure handles don't cross
                if (handleIndex === 0 && newValue > innerValue[1]) {
                    newRangeValue[0] = innerValue[1];
                } else if (handleIndex === 1 && newValue < innerValue[0]) {
                    newRangeValue[1] = innerValue[0];
                }

                setInnerValue(newRangeValue);
                onChange?.(newRangeValue);
            } else if (!isRange) {
                setInnerValue(newValue);
                onChange?.(newValue);
            }
        },
        [isRange, innerValue, onChange],
    );

    const handleMouseDown = useCallback(
        (handleIndex) => (e) => {
            if (isReadOnly) return;
            e.preventDefault();
            setActiveHandle(handleIndex);
        },
        [isReadOnly],
    );

    const handleTouchStart = useCallback(
        (handleIndex) => (e) => {
            if (isReadOnly) return;
            e.preventDefault();
            setActiveHandle(handleIndex);
        },
        [isReadOnly],
    );

    useEffect(() => {
        if (activeHandle === null) return;

        const handleMouseMove = (e) => {
            const newValue = getValueFromPosition(e.clientX);
            updateValue(newValue, activeHandle);
        };

        const handleTouchMove = (e) => {
            const touch = e.touches[0];
            const newValue = getValueFromPosition(touch.clientX);
            updateValue(newValue, activeHandle);
        };

        const handleMouseUp = () => {
            setActiveHandle(null);
            props.onBlur && props.onBlur();
        };

        const handleTouchEnd = () => {
            setActiveHandle(null);
            props.onBlur && props.onBlur();
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [activeHandle, getValueFromPosition, updateValue, props]);

    const handleRailClick = useCallback(
        (e) => {
            if (isReadOnly || e.target.hasAttribute('data-slider-handle')) return;
            const newValue = getValueFromPosition(e.clientX);

            if (isRange && Array.isArray(innerValue)) {
                const [val1, val2] = innerValue;
                const dist1 = Math.abs(newValue - val1);
                const dist2 = Math.abs(newValue - val2);
                const closestHandle = dist1 < dist2 ? 0 : 1;
                updateValue(newValue, closestHandle);
            } else if (!isRange) {
                updateValue(newValue);
            }
        },
        [isReadOnly, isRange, innerValue, getValueFromPosition, updateValue],
    );

    const formatValue = useCallback(
        (val) => {
            if (tipFormatter) return tipFormatter(val);
            return val;
        },
        [tipFormatter],
    );

    const renderHandle = useCallback(
        (val, index = 0) => {
            const position = getPositionFromValue(val);
            const positionStyle = { left: `${position}%` };
            const isDragging = activeHandle === index;

            return (
                <div key={index} className={classes.handleContainer} {...override.handleContainer}>
                    {showValue && (
                        <span style={positionStyle} className={classes.label} {...override.label}>
                            {formatValue(val)}
                        </span>
                    )}
                    <div
                        style={positionStyle}
                        className={classnames(classes.handle, {
                            [classes.handleActive]: isDragging,
                        })}
                        data-slider-handle="true"
                        onMouseDown={handleMouseDown(index)}
                        onTouchStart={handleTouchStart(index)}
                        role="slider"
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuenow={val}
                        tabIndex={isReadOnly ? -1 : 0}
                        {...override.handle}
                    />
                </div>
            );
        },
        [
            getPositionFromValue,
            activeHandle,
            classes.handleContainer,
            classes.label,
            classes.handle,
            classes.handleActive,
            handleMouseDown,
            handleTouchStart,
            formatValue,
            showValue,
            min,
            max,
            isReadOnly,
            override.handleContainer,
            override.label,
            override.handle,
        ],
    );

    const trackStyle = useMemo(() => {
        if (isRange) {
            const [val1, val2] = Array.isArray(innerValue) ? innerValue : [min, max];
            const left = getPositionFromValue(val1);
            const right = getPositionFromValue(val2);
            return { left: `${left}%`, width: `${right - left}%` };
        } else {
            const width = getPositionFromValue(innerValue);
            return { left: '0%', width: `${width}%` };
        }
    }, [isRange, innerValue, getPositionFromValue, min, max]);

    return (
        <InputWrapper {...props} error={error} className={rootClassName} overrides={overridesProp}>
            <div
                className={classes.slider}
                ref={sliderRef}
                onClick={handleRailClick}
                {...override.slider}
            >
                <div className={classes.rail} {...override.rail} />
                <div className={classes.track} style={trackStyle} {...override.track} />
                {isRange && Array.isArray(innerValue) ? (
                    <>
                        {renderHandle(innerValue[0], 0)}
                        {renderHandle(innerValue[1], 1)}
                    </>
                ) : (
                    renderHandle(Array.isArray(innerValue) ? innerValue[0] : innerValue, 0)
                )}
            </div>
        </InputWrapper>
    );
}

Slider.overrides = ['root', 'slider', 'rail', 'track', 'handleContainer', 'label', 'handle'];

Slider.propTypes = {
    /** Custom className for styling purposes */
    className: PropTypes.string,
    /** Error message displayed below the component, also triggers error styling when present */
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /** Tooltip text displayed in an info icon next to the label */
    hint: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /** When true, slider will take up 100% of the available width */
    isFullWidth: PropTypes.bool,
    /** When true, renders the slider as a range selector with two handles */
    isRange: PropTypes.bool,
    /** When true, prevents user from changing the slider value */
    isReadOnly: PropTypes.bool,
    /** Text to be displayed as the slider's label */
    label: PropTypes.string,
    /** Determines how the label is positioned relative to the slider */
    labelMode: PropTypes.oneOf(['horizontal', 'vertical']),
    /** The maximum value the slider can represent */
    max: PropTypes.number,
    /** The minimum value the slider can represent */
    min: PropTypes.number,
    /** Function called when the slider value changes */
    onChange: PropTypes.func,
    /** Object with custom style overrides for inner elements */
    overrides: PropTypes.object,
    /** When true, displays the current value above the slider handle */
    showValue: PropTypes.bool,
    /** Value increment/decrement for each step of the slider. Must be greater than zero, and max - min should be evenly divisible by the step value. */
    step: PropTypes.number,
    /** Custom function to format the value display (replaces isPercentage) */
    tipFormatter: PropTypes.func,
    /** Current value of the slider (for range: array with two values, for single: number) */
    value: PropTypes.any,
};

export default React.memo(Slider);
